import {
  ApiClientError,
  createApiClient,
  getToken,
  getTokenVersion,
  type RequestOptions,
} from '@budgeting101/api-client';
import { z } from 'zod';

/** Shared production client for authenticated finance routes. */
export const api = createApiClient({ baseUrl: '/api' });

/**
 * Migration bridge for endpoints whose response contract still lives beside
 * its route. New endpoints should replace this with an explicit Zod schema.
 */
export function apiSchema<T>(): z.ZodType<T> {
  return z.custom<T>();
}

/** Runtime-check the top-level collection while endpoint fields migrate. */
export function apiArraySchema<T>(): z.ZodType<T[]> {
  return z.array(z.custom<T>());
}

/** Runtime-check the top-level object while endpoint fields migrate. */
export function apiObjectSchema<T extends object>(): z.ZodType<T> {
  return z.custom<T>((value) => value !== null && typeof value === 'object' && !Array.isArray(value));
}

export const emptyResponseSchema = z.undefined();

export interface CursorPageInfo {
  count: number;
  limit: number;
  hasNextPage: boolean;
  nextCursor: string | null;
  sort: string | null;
}

export interface CursorPage<T> {
  items: T[];
  pageInfo: CursorPageInfo;
}

function errorCodeForStatus(status: number): string {
  if (status === 400) return 'INVALID_REQUEST';
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 422) return 'VALIDATION';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER_ERROR';
  return 'UNKNOWN';
}

async function rawError(response: Response): Promise<ApiClientError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  const envelope = payload && typeof payload === 'object' && 'error' in payload
    ? (payload as { error: unknown }).error
    : payload;
  const raw = envelope && typeof envelope === 'object'
    ? envelope as { code?: unknown; message?: unknown; details?: unknown; referenceId?: unknown }
    : undefined;
  return new ApiClientError({
    status: response.status,
    code: typeof raw?.code === 'string' ? raw.code : errorCodeForStatus(response.status),
    message: typeof raw?.message === 'string' ? raw.message : 'The request failed.',
    details: raw?.details,
    referenceId: typeof raw?.referenceId === 'string' ? raw.referenceId : undefined,
  }, response.status);
}

function authenticatedHeaders(options?: RequestOptions): Headers {
  const headers = new Headers({ Accept: 'application/json' });
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (options?.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey);
  return headers;
}

function assertOnlineMutation(): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiClientError({
      status: 0,
      code: 'OFFLINE',
      message: 'You are offline. Nothing was sent — your draft is kept on this device.',
    }, 0);
  }
}

async function rawFetch(path: string, init: RequestInit, mutating: boolean): Promise<Response> {
  if (mutating) assertOnlineMutation();
  const requestTokenVersion = getTokenVersion();
  let response: Response;
  try {
    response = await fetch(new URL(`/api${path}`, window.location.origin), init);
  } catch {
    throw new ApiClientError({
      status: 0,
      code: mutating ? 'NETWORK_UNCONFIRMED' : 'NETWORK',
      message: mutating
        ? 'The connection dropped while sending. The result is unknown — refresh the relevant list and check for the result before retrying.'
        : 'Could not reach the server.',
    }, 0);
  }
  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:expired', {
      detail: { tokenVersion: requestTokenVersion },
    }));
  }
  if (!response.ok) throw await rawError(response);
  return response;
}

function nonNegativeHeader(value: string | null, fallback: number): number {
  if (value === null) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * Read an array endpoint whose cursor contract is carried in response headers.
 * This keeps pagination metadata explicit without weakening the item schema.
 */
export async function getCursorPage<T>(
  path: string,
  schema: z.ZodType<T[]>,
  params: Record<string, string | number | boolean | null | undefined> = {},
): Promise<CursorPage<T>> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  }
  const response = await rawFetch(`${path}${query.size > 0 ? `?${query.toString()}` : ''}`, {
    method: 'GET',
    headers: authenticatedHeaders(),
  }, false);
  const items = schema.parse(await response.json());
  const hasNextPage = response.headers.get('x-has-more') === 'true';
  return {
    items,
    pageInfo: {
      count: nonNegativeHeader(response.headers.get('x-page-count'), items.length),
      limit: nonNegativeHeader(response.headers.get('x-page-limit'), items.length),
      hasNextPage,
      nextCursor: hasNextPage ? response.headers.get('x-next-cursor') : null,
      sort: response.headers.get('x-sort'),
    },
  };
}

/** Multipart companion to the JSON client, with the same auth/offline/error contract. */
export async function uploadForm<T>(
  path: string,
  form: FormData,
  schema: z.ZodType<T>,
  options: RequestOptions,
): Promise<T> {
  const response = await rawFetch(path, {
    method: 'POST',
    headers: authenticatedHeaders(options),
    body: form,
  }, true);
  return schema.parse(await response.json());
}

/** Authenticated binary fetch used by evidence downloads. */
export async function downloadBlob(path: string): Promise<Blob> {
  const response = await rawFetch(path, {
    method: 'GET',
    headers: authenticatedHeaders(),
  }, false);
  return response.blob();
}
